import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUISettings } from '../context/UISettingsContext';

const STORAGE_KEY = 'aid_cookie_consent_v1';

export function hasAcknowledgedCookies(): boolean {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY));
  } catch {
    return false;
  }
}

function acknowledge() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ necessary: true, acknowledgedAt: new Date().toISOString(), version: 2 }),
    );
  } catch {
    // storage unavailable — banner will simply reappear next visit
  }
}

export default function CookieBanner() {
  const { t, resolvedTheme } = useUISettings();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hasAcknowledgedCookies()) {
      setVisible(true);
    }
  }, []);

  const accept = useCallback(() => {
    acknowledge();
    setVisible(false);
  }, []);

  if (!visible) return null;

  const isDark = resolvedTheme === 'dark';

  const panelStyle = isDark
    ? {
        border: '1px solid rgba(129, 140, 248, 0.32)',
        background: 'linear-gradient(120deg, rgba(12,18,32,0.97), rgba(30,41,59,0.96))',
        color: '#e2e8f0',
        boxShadow: '0 24px 60px -22px rgba(79, 70, 229, 0.6)',
      }
    : {
        border: '1px solid rgba(79, 70, 229, 0.22)',
        background: 'linear-gradient(120deg, rgba(248,250,252,0.99), rgba(238,242,255,0.98))',
        color: '#0f172a',
        boxShadow: '0 28px 80px -34px rgba(79, 70, 229, 0.35)',
      };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t('cookies.title')}
      className="fixed bottom-3 left-3 right-3 z-[80] mx-auto w-auto max-w-[min(640px,calc(100vw-1.5rem))] overflow-hidden rounded-md px-4 py-3 md:left-auto md:right-3"
      style={panelStyle}
    >
      <div className="flex flex-col gap-2">
        <p
          className="text-[8px] font-black uppercase tracking-[0.14em]"
          style={{ color: isDark ? 'rgba(165, 180, 252, 0.95)' : '#3730a3' }}
        >
          {t('cookies.title')}
        </p>
        <p className="text-[11px] leading-snug">
          {t('cookies.body')}{' '}
          <Link
            to="/privacy"
            className="font-bold underline-offset-2 hover:underline"
            style={{ color: isDark ? '#a5b4fc' : '#4338ca' }}
          >
            {t('cookies.policyLink')}
          </Link>
        </p>

        <div className="flex flex-wrap items-center justify-end gap-1.5 pt-1">
          <button
            type="button"
            onClick={accept}
            className="rounded-sm bg-[var(--app-accent)] px-3 py-1 text-[9px] font-bold uppercase tracking-[0.1em] text-white transition-all hover:bg-[var(--app-accent-strong)]"
          >
            {t('cookies.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
